// Feature flags via Unleash React SDK.
// Flags are fetched from the Unleash Frontend API at runtime.
// Toggle flags from the Unleash admin UI — no redeploy needed.

import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'

// Hard ceiling for the Suspense flag-gate. If the Unleash frontend fetch hangs
// (observed inside the Android WebView), the gate MUST still release so the app
// renders rather than showing a permanent blank screen. Flag VALUES stay
// fail-closed (default false) — only the GATE fails open. Exported for tests.
export const FLAG_GATE_TIMEOUT_MS = 3000

let flagsPromise: Promise<void> | null = null
let flagsSettled = false
let resolveFlags: (() => void) | null = null

function getFlagsPromise(): Promise<void> {
  if (!flagsPromise) {
    flagsPromise = new Promise<void>((resolve) => {
      resolveFlags = resolve
      // Safety timeout — don't block the app if Unleash is unreachable.
      setTimeout(() => settleFlags(), FLAG_GATE_TIMEOUT_MS)
    })
  }
  return flagsPromise
}

function settleFlags() {
  if (!flagsSettled) {
    flagsSettled = true
    resolveFlags?.()
  }
}

/**
 * Suspense-compatible hook — suspends until Unleash flags are fetched.
 * Wrap the consuming tree in <Suspense fallback={...}>.
 * Falls through on error or timeout so the app still renders (flags default to false).
 */
export function useFeatureFlags() {
  const { flagsReady, flagsError } = useFlagsStatus()

  // All hooks must be called unconditionally (Rules of Hooks)
  // EP-111 ops kill-switch — `shared_owners` is the per-env hard gate
  // for the co-owners / common-area-fees feature set (Layer 4 of the four-layer
  // gate). Other layers: account.account_type (Layer 1, unchanged),
  // account.settings.condominium_enabled (Layer 2, per-account toggle),
  // property.property_type (Layer 3, client type-tag only).
  const sharedOwners = useFlag('shared_owners')
  const cmms = useFlag('cmms')
  const eTaxInvoice = useFlag('e_tax_invoice')
  const testPayment20thb = useFlag('test_payment_20thb')
  const debtCollection = useFlag('debt_collection')
  const lateFees = useFlag('late_fees')
  const announcements = useFlag('announcements')
  const leadGeneration = useFlag('lead_generation')
  // EP-73 AI Feature Coach — ops kill-switch half of the dual gate
  // (the other half is accountSettings.settings.ai_coach_enabled)
  const aiCoach = useFlag('ai_coach')
  const loyaltyPoints = useFlag('loyalty_points')
  const renewalBenefits = useFlag('renewal_benefits')
  // EP-99 win-pickup relay — ops kill-switch half of the dual gate
  // (the other half is accountSettings.settings.win_pickup_enabled)
  const winPickup = useFlag('win_pickup')
  // EP-133 visitor management — ops kill-switch half of the dual gate
  // (the other half is accountSettings.settings.visitor_management_enabled).
  // The server enforces BOTH via VisitorManagementModuleGate; this flag was
  // never declared here, so the sidebar and dashboard rendered the surface
  // for every account while every request behind it returned 403.
  const visitorManagement = useFlag('visitor_management')

  // Resolve on ready OR error — don't block the app if Unleash is unreachable
  if (flagsReady || flagsError) {
    settleFlags()
  }

  // Suspend only while flags are still loading (with 3s timeout safety net)
  if (!flagsSettled) {
    throw getFlagsPromise()
  }

  return {
    sharedOwners,
    cmms,
    eTaxInvoice,
    testPayment20thb,
    debtCollection,
    lateFees,
    announcements,
    leadGeneration,
    aiCoach,
    loyaltyPoints,
    renewalBenefits,
    winPickup,
    visitorManagement,
  }
}

export type FeatureFlags = ReturnType<typeof useFeatureFlags>
